/**
 * Client helpers for invoice match tolerance settings UI.
 * Express stores whole pennies; the modal edits pounds.
 */

export const INVOICE_MATCH_TOLERANCE_DEFAULT_PENNIES = 100
export const MAX_INVOICE_MATCH_TOLERANCE_PENNIES = 5000 // £50.00

export function penniesToPounds(pennies) {
  if (pennies == null || pennies === '') {
    return null
  }
  const value = Number(pennies)
  if (!Number.isFinite(value)) {
    return null
  }
  return Math.round(value) / 100
}

export function poundsToPennies(pounds) {
  if (pounds == null || pounds === '') {
    return null
  }
  const value = Number(pounds)
  if (!Number.isFinite(value)) {
    return null
  }
  return Math.round(value * 100)
}

export function formatTolerancePounds(pennies) {
  const pounds = penniesToPounds(pennies)
  if (pounds == null) {
    return '—'
  }
  return `£${pounds.toFixed(2)}`
}

export function isValidTolerancePennies(pennies, { nullable = false } = {}) {
  if (nullable && pennies === null) {
    return true
  }
  if (pennies == null || pennies === '') {
    return false
  }
  const value = Number(pennies)
  return (
    Number.isInteger(value) &&
    value >= 0 &&
    value <= MAX_INVOICE_MATCH_TOLERANCE_PENNIES
  )
}

export function countSupplierToleranceOverrides(supplierOverrides = []) {
  return (supplierOverrides || []).filter(
    row => row?.invoiceMatchTolerance != null,
  ).length
}

/**
 * Badge copy under the invoices date picker.
 * e.g. "Match tolerance: £1.00" or "Match tolerance: £1.00 · 2 supplier overrides"
 */
export function buildToleranceBadgeLabel(settings) {
  // Default params don't cover null (e.g. settings failed to load)
  const safe = settings || {}
  const venuePennies =
    safe.invoiceMatchTolerance ??
    safe.systemDefaultInvoiceMatchTolerance ??
    INVOICE_MATCH_TOLERANCE_DEFAULT_PENNIES
  const overrideCount = countSupplierToleranceOverrides(safe.supplierOverrides)
  const base = `Match tolerance: ${formatTolerancePounds(venuePennies)}`
  if (!overrideCount) {
    return base
  }
  return `${base} · ${overrideCount} supplier override${
    overrideCount === 1 ? '' : 's'
  }`
}

/**
 * Local modal draft from GET reconciliation-settings payload.
 * Supplier overrideDraft is pounds string/number or null (= inherit venue).
 */
export function buildToleranceModalDraft(settings) {
  // Default params don't cover null (e.g. settings failed to load)
  const safe = settings || {}
  return {
    venueTolerancePounds: penniesToPounds(
      safe.invoiceMatchTolerance ??
        safe.systemDefaultInvoiceMatchTolerance ??
        INVOICE_MATCH_TOLERANCE_DEFAULT_PENNIES,
    ),
    suppliers: (safe.supplierOverrides || []).map(row => ({
      venueSupplierId: row.venueSupplierId,
      supplierTitle: row.supplierTitle || 'Supplier',
      overridePounds:
        row.invoiceMatchTolerance == null
          ? null
          : penniesToPounds(row.invoiceMatchTolerance),
      effectivePennies: row.effectiveInvoiceMatchTolerance,
    })),
  }
}

/**
 * Diff draft against original settings → API save jobs.
 * Returns { venuePennies?, supplierUpdates: [{ venueSupplierId, invoiceMatchTolerance }] }
 */
export function buildToleranceSavePlan(draft, originalSettings) {
  const safeDraft = draft || {}
  const safeOriginal = originalSettings || {}
  const originalVenue =
    safeOriginal.invoiceMatchTolerance ??
    safeOriginal.systemDefaultInvoiceMatchTolerance ??
    INVOICE_MATCH_TOLERANCE_DEFAULT_PENNIES

  const venuePennies = poundsToPennies(safeDraft.venueTolerancePounds)
  if (!isValidTolerancePennies(venuePennies)) {
    throw new Error(
      `Venue tolerance must be between £0.00 and £${(
        MAX_INVOICE_MATCH_TOLERANCE_PENNIES / 100
      ).toFixed(2)}`,
    )
  }

  const plan = {
    venuePennies: venuePennies === originalVenue ? undefined : venuePennies,
    supplierUpdates: [],
  }

  const originalById = new Map(
    (safeOriginal.supplierOverrides || []).map(row => [
      String(row.venueSupplierId),
      row.invoiceMatchTolerance == null
        ? null
        : Math.round(Number(row.invoiceMatchTolerance)),
    ]),
  )

  for (const supplier of safeDraft.suppliers || []) {
    const id = String(supplier.venueSupplierId)
    const nextPennies =
      supplier.overridePounds == null || supplier.overridePounds === ''
        ? null
        : poundsToPennies(supplier.overridePounds)

    if (nextPennies !== null && !isValidTolerancePennies(nextPennies)) {
      throw new Error(
        `Tolerance for ${
          supplier.supplierTitle || 'supplier'
        } must be between £0.00 and £${(
          MAX_INVOICE_MATCH_TOLERANCE_PENNIES / 100
        ).toFixed(2)}`,
      )
    }

    const previous = originalById.has(id) ? originalById.get(id) : null
    if (previous !== nextPennies) {
      plan.supplierUpdates.push({
        venueSupplierId: id,
        invoiceMatchTolerance: nextPennies,
      })
    }
  }

  return plan
}
